import { useState, ChangeEventHandler, KeyboardEventHandler } from "react";
import produce from "immer";
import { QRCodeSVG } from "qrcode.react";
import { animated, useSpring } from "@react-spring/web";
import AppsOutageOutlinedIcon from "@mui/icons-material/AppsOutageOutlined";
import AppSettingsAltOutlinedIcon from "@mui/icons-material/AppSettingsAltOutlined";
import { AppPlatform } from "app/types";
import Constants from "common/constant";
import { usePlaySound, btnDelay } from "common/utils";
import i18n from "features/parent/i18n";
import { TutorialInApp } from "features/tutorial";
import { ParentModal, CourseAnswer } from "features/parent/components";
import { AppleBadge, GooglePlayBadge, AmazonBadge } from "../assets";

enum AppStore {
  APPLE = "apple",
  AMAZON = "amazon",
  GOOGLE = "google",
  WEB = "web",
}

export const OtherTab = () => {
  const PASSWORD_LENGTH = 6;
  const play = usePlaySound();
  const [tutorialShow, setTutorialShow] = useState(false);
  const [courseAnswerShow, setCourseAnswerShow] = useState(false);
  const [labShow, setLabShow] = useState(false);
  const [labError, setLabError] = useState(false);
  const [labDisabled, setLabDisabled] = useState(false);
  const [labPassword, setLabPassword] = useState(["", "", "", "", "", ""]);
  const [labAnimationStyles, labAnimationApi] = useSpring(() => ({
    from: { x: 0 },
  }));

  const handleOpenStore = (store: AppStore) => () => {
    play();
    if ("ReactNativeWebView" in window) {
      switch (store) {
        case AppStore.APPLE:
          if (window.kidsAppPlatform === "ios" && Number(window.kidsBuildVersion) > 73) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                command: "openApp",
                data: {
                  schema: "com.inexus.app.parents://",
                  url: "https://apps.apple.com/jp/app/id1642655214",
                },
              }),
            );
          } else {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                command: "openLink",
                data: {
                  url: "https://apps.apple.com/jp/app/id1642655214",
                },
              }),
            );
          }
          break;
        case AppStore.GOOGLE:
          if (window.kidsAppPlatform === "android" && Number(window.kidsBuildVersion) > 58) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                command: "openApp",
                data: {
                  schema: "com.inexus.app.parents://",
                  url: "https://play.google.com/store/apps/details?id=com.inexus.app.parents",
                },
              }),
            );
          } else {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                command: "openLink",
                data: {
                  url: "https://play.google.com/store/apps/details?id=com.inexus.app.parents",
                },
              }),
            );
          }
          break;
        case AppStore.AMAZON:
          if (window.kidsAppPlatform === "amazon" && Number(window.kidsBuildVersion) > 52) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                command: "openApp",
                data: {
                  schema: "com.inexus.app.parents://",
                  url: "amzn://apps/android?p=com.inexus.app.parents",
                },
              }),
            );
          } else {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                command: "openLink",
                data: {
                  url: "http://www.amazon.co.jp/gp/mas/dl/android?p=com.inexus.app.parents",
                },
              }),
            );
          }
          break;
        default:
          break;
      }
    } else {
      switch (store) {
        case AppStore.APPLE:
          window.open("https://apps.apple.com/jp/app/id1642655214", "_blank");
          break;
        case AppStore.GOOGLE:
          window.open(
            "https://play.google.com/store/apps/details?id=com.inexus.app.parents",
            "_blank",
          );
          break;
        case AppStore.AMAZON:
          window.open(
            "http://www.amazon.co.jp/gp/mas/dl/android?p=com.inexus.app.parents",
            "_blank",
          );
          break;
        case AppStore.WEB:
          window.open(Constants.parentsHost, "_blank");
          break;
        default:
          break;
      }
    }
  };

  const handleCourseAnswer = () => {
    play();
    btnDelay(() => {
      setCourseAnswerShow(true);
    });
  };

  const handleCloseCourseAnswer = () => {
    play();
    btnDelay(() => setCourseAnswerShow(false));
  };

  const handleTutorial = () => {
    play();
    btnDelay(() => {
      setTutorialShow(true);
    });
  };

  const handleCloseTutorial = () => {
    play();
    btnDelay(() => setTutorialShow(false));
  };

  const handleEnableLab = () => {
    play();
    btnDelay(() => {
      setLabShow(true);
    });
  };

  const handleCloseLab = () => {
    play();
    btnDelay(() => {
      setLabShow(false);
      setLabError(false);
      setLabPassword(["", "", "", "", "", ""]);
    });
  };

  const handleLabPasswordChange =
    (index: number): ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      setLabError(false);
      const next = e.target.nextElementSibling as HTMLInputElement;
      if (e.target.value || e.target.value === "") {
        setLabPassword(
          produce(labPassword, (draft) => {
            draft[index] = e.target.value;
          }),
        );
        if (next && e.target.value !== "") {
          next.focus();
        }
      }
    };

  const handleLabSubmit = () => {
    if (!labDisabled && !labError) {
      setLabDisabled(true);
      setLabError(true);
      setLabDisabled(false);
      labAnimationApi.start({
        to: async (next) => {
          await next({ x: 10 });
          await next({ x: -10 });
          await next({ x: 10 });
          await next({ x: -10 });
        },
        config: { duration: 100 },
      });
    }
  };

  const handleLabEnterKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.keyCode === 13 && !(labPassword.length === 0 || labError)) {
      handleLabSubmit();
    }
  };

  return (
    <>
      {tutorialShow && <TutorialInApp close={handleCloseTutorial} />}
      <ParentModal
        open={courseAnswerShow}
        onClose={handleCloseCourseAnswer}
        title={i18n.t("MSG_SETTING_TITLE_COURSE_ANSWER")}
        icon="menu_book"
      >
        <CourseAnswer />
      </ParentModal>
      <ParentModal
        open={labShow}
        onClose={handleCloseLab}
        title={i18n.t("MSG_SETTING_TITLE_CORPORATE_AUTH")}
        icon="science"
      >
        <div className="flex-col-view items-center w-full">
          <div className="flex-col-el w-full px-6 gap-8">
            <div className="flex-row-el flex-center">
              <p className="text-[24px] text-textcolor-black text-center">
                {i18n.t("MSG_PARENT_PAGE_CORPORATE_AUTH_DESCRIPTION")}
              </p>
            </div>

            <div className="relative flex-row-el gap-4 flex-center w-full py-6 bg-white rounded-[16px] shadow-sm border border-outline">
              <div className="flex-row-el flex-center px-[8px] h-[28px] bg-orange-alt rounded-[14px] z-10 absolute -top-[14px] left-4">
                <AppSettingsAltOutlinedIcon />
                <p className="text text-textcolor-black !text-[24px] !px-[8px]">
                  {i18n.t("MSG_PARENT_PAGE_CORPORATE_AUTH_CODE_INPUT")}
                </p>
              </div>

              <div className="flex-col-center">
                <p className="text text-textcolor-black !leading-[30px]">
                  {i18n.t("MSG_SETTING_PARENTS_LINK_ADD_USER_AUTH_CODE_TITLE")}
                </p>
                <animated.div
                  className="flex-row-center"
                  style={labAnimationStyles}
                  onKeyDown={handleLabEnterKeyDown}
                >
                  {Array(PASSWORD_LENGTH)
                    .fill(0)
                    .map((_, index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength={1}
                        value={labPassword[index]}
                        onChange={handleLabPasswordChange(index)}
                        className={`relative flex-col-el flex-center w-14 outline-none appearance-none overflow-hidden border-solid border-[2px] border-l-0 border-gray-40 rounded-none first:border-l-[2px] first:rounded-l-[8px] last:rounded-r-[8px] text-body text-textcolor-black text-center text-[48px] ${
                          labError && "!border-danger"
                        }`}
                      />
                    ))}
                </animated.div>
              </div>

              <div className="flex-row-el flex-center mt-8">
                <button
                  onClick={handleLabSubmit}
                  className="flex-row-el items-center justify-center w-[200px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={labPassword.length === 0 || labError}
                >
                  {i18n.t("MSG_PARENT_PAGE_CORPORATE_CONFIRM")}
                </button>
              </div>
            </div>

            <div className="relative flex-row-el gap-4 items-center justify-start w-full bg-white p-8 rounded-[16px] shadow-sm border border-outline">
              <div className="flex-row-el flex-center px-[8px] h-[28px] bg-orange-alt rounded-[14px] z-10 absolute -top-[14px] left-4">
                <AppsOutageOutlinedIcon />
                <p className="text text-textcolor-black !text-[24px] !px-[8px]">
                  {i18n.t("MSG_PARENT_PAGE_CORPORATE_ALLOWED_CONTENT")}
                </p>
              </div>

              <div className="flex-row-center gap-2 h-[168px]"></div>

              <div className="flex-row-el flex-center absolute -z-1 w-full">
                <p className="text-body text-[36px] text-textcolor-black">
                  {i18n.t("MSG_PARENT_PAGE_CORPORATE_NO_CONTENT")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </ParentModal>
      <div className="grid grid-cols-2 gap-6">
        {/* 保護者アプリ */}
        <div className="bg-white rounded-[16px] p-6">
          <div className="flex-row-el items-center gap-2 text-[24px] text-textcolor-black mb-4">
            <span className="material-symbols-outlined !text-[45px] text-red-100">download</span>
            {i18n.t("MSG_PARENT_PAGE_DOWNLOAD_PARENTS_APP")}
          </div>
          <div className="flex-row-el items-center gap-5">
            {/* QRコード */}
            <div className="flex-col-el flex-center w-[120px] h-[120px] bg-white rounded-lg p-2">
              <QRCodeSVG size={104} value={Constants.parentsLinkPageHost} />
            </div>
            <div className="flex-1">
              <p className="text-[21px] text-textcolor-black mb-3">
                {i18n.t("MSG_PARENT_PAGE_APP_DESCRIPTION")}
              </p>
              {/* Store Badges */}
              <div className="flex-row-el flex-wrap items-center justify-center gap-2">
                {/* ブラウザまたはiOS環境でAppleバッジ表示 */}
                {(!("ReactNativeWebView" in window) ||
                  window.kidsAppPlatform === AppPlatform.iOS) && (
                  <div
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={handleOpenStore(AppStore.APPLE)}
                  >
                    <AppleBadge className="h-[40px] w-auto" />
                  </div>
                )}
                {/* ブラウザまたはAndroid環境でGoogle Playバッジ表示 */}
                {(!("ReactNativeWebView" in window) ||
                  window.kidsAppPlatform === AppPlatform.Android) && (
                  <div
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={handleOpenStore(AppStore.GOOGLE)}
                  >
                    <GooglePlayBadge className="h-[52px] w-auto" />
                  </div>
                )}
                {/* ブラウザまたはAmazon環境でAmazonバッジ表示 */}
                {(!("ReactNativeWebView" in window) ||
                  window.kidsAppPlatform === AppPlatform.Amazon) && (
                  <div
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={handleOpenStore(AppStore.AMAZON)}
                  >
                    <AmazonBadge className="h-[40px] w-auto" />
                  </div>
                )}
              </div>
              {/* Web試用ボタン - ブラウザ環境のみ表示 */}
              {!("ReactNativeWebView" in window) && (
                <div
                  className="mt-3 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={handleOpenStore(AppStore.WEB)}
                >
                  <img
                    alt="web trial"
                    className="h-[40px] w-auto"
                    src={`${Constants.assetHost}/assets/images/parents_web_link.png`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* コンテンツ関連 */}
        <div className="bg-white rounded-[16px] p-6">
          <div className="flex-row-el items-center gap-2 text-[24px] text-textcolor-black mb-4">
            <span className="material-symbols-outlined !text-[45px] text-red-100">article</span>
            {i18n.t("MSG_PARENT_PAGE_LEARNING_SUPPORT")}
          </div>
          <div className="flex-col-el gap-3">
            <button
              className="flex-row-el items-center gap-3 p-4 bg-white rounded-[12px] border border-gray2-60 hover:bg-gray2-20 transition-all w-full text-left cursor-pointer"
              onClick={handleCourseAnswer}
            >
              <span className="material-symbols-outlined text-[36px] text-textcolor-black">
                menu_book
              </span>
              <span className="text-[24px] text-textcolor-black">
                {i18n.t("MSG_PARENT_PAGE_COURSE_ANSWER")}
              </span>
            </button>
            <button
              className="flex-row-el items-center gap-3 p-4 bg-white rounded-[12px] border border-gray2-60 hover:bg-gray2-20 transition-all w-full text-left cursor-pointer"
              onClick={handleTutorial}
            >
              <span className="material-symbols-outlined text-[36px] text-textcolor-black">
                play_circle
              </span>
              <span className="text-[24px] text-textcolor-black">
                {i18n.t("MSG_PARENT_PAGE_VIEW_TUTORIAL")}
              </span>
            </button>
          </div>
        </div>

        {/* 企業連携 */}
        <div className="bg-white rounded-[16px] p-6">
          <div className="flex-row-el items-center gap-2 text-[24px] text-textcolor-black mb-4">
            <span className="material-symbols-outlined !text-[45px] text-red-100">science</span>
            {i18n.t("MSG_PARENT_PAGE_CORPORATE_CODE")}
          </div>
          <div className="flex-col-el gap-3">
            <button
              className="flex-row-el items-center gap-3 p-4 bg-white rounded-[12px] border border-gray2-60 hover:bg-gray2-20 transition-all w-full text-left cursor-pointer"
              onClick={handleEnableLab}
            >
              <span className="material-symbols-outlined text-[36px] text-textcolor-black">
                rocket_launch
              </span>
              <span className="text-[24px] text-textcolor-black">
                {i18n.t("MSG_PARENT_PAGE_ENABLE_CORPORATE")}
              </span>
            </button>
          </div>
          <p className="text-[19px] text-gray2-100 mt-3">
            {i18n.t("MSG_PARENT_PAGE_CORPORATE_DESCRIPTION")}
          </p>
        </div>
      </div>
    </>
  );
};
